import { useState } from 'react';
import {
    deriveSyncPanelMode,
    hasVisibleNodeChanges,
    hasVisiblePluginContent,
    hasAnyStatisticsChanges,
    computeTotalsFromStatistics,
} from './syncStatusUtils';
import DiffSelector from './DiffSelector';
import styles from '../GirderConnection.module.css';

function PluginMessages({ pluginName, pluginData }) {
    const [expanded, setExpanded] = useState(false);
    if (!pluginData?.summary && !(pluginData?.messages?.length)) return null;

    const messages = pluginData.messages || [];
    const hasDetails = messages.length > 0;

    return (
        <div className={styles.syncStatusPluginMessage}>
            <div className={styles.syncStatusPluginMessageHeader}>
                <strong>{pluginName.toUpperCase()}:</strong>
                {pluginData.summary && (
                    <span className={styles.syncStatusPluginSummary}>{pluginData.summary}</span>
                )}
                {hasDetails && (
                    <button
                        type="button"
                        className={styles.syncStatusPluginExpandButton}
                        onClick={() => setExpanded(!expanded)}
                        title={expanded ? 'Collapse details' : 'Expand details'}
                    >
                        {expanded ? '▼' : '▶'}
                    </button>
                )}
            </div>
            {expanded && hasDetails && (
                <div className={styles.syncStatusPluginDetails}>
                    {messages.map((message, index) => (
                        <div key={index} className={styles.syncStatusPluginDetailMessage}>
                            {message}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function NodeBreakdown({ statistics, pluginData, tense }) {
    const byNode = statistics?.byNode || {};

    const nodes = Object.entries(byNode).filter(([, nodeData]) =>
        hasVisibleNodeChanges(nodeData)
    );

    if (nodes.length === 0 && !hasVisiblePluginContent(pluginData)) return null;

    return (
        <div className={styles.syncStatusByNode}>
            {nodes.map(([nodeUuid, nodeData]) => (
                <div key={nodeUuid} className={styles.syncStatusNodeGroup}>
                    <div className={styles.syncStatusNodeName}>
                        <strong>Network — {nodeData.nodeName}</strong>
                    </div>
                    <div className={styles.syncStatusNodeChanges}>
                        {nodeData.foldersToAdd > 0 && (
                            <div>📁 {tense === 'future' ? 'New Folders' : 'New Folders'}: {nodeData.foldersToAdd}</div>
                        )}
                        {nodeData.itemsToAdd > 0 && (
                            <div>📄 {tense === 'future' ? 'New Items' : 'New Items'}: {nodeData.itemsToAdd}</div>
                        )}
                        {nodeData.foldersToUpdate > 0 && (
                            <div>🔄 {tense === 'future' ? 'Folders to Update' : 'Folders Updated'}: {nodeData.foldersToUpdate}</div>
                        )}
                        {nodeData.itemsToUpdate > 0 && (
                            <div>🔄 {tense === 'future' ? 'Items to Update' : 'Items Updated'}: {nodeData.itemsToUpdate}</div>
                        )}
                        {nodeData.foldersToRemove > 0 && (
                            <div>🗑️ {tense === 'future' ? 'Folders to Remove' : 'Folders Removed'}: {nodeData.foldersToRemove}</div>
                        )}
                        {nodeData.itemsToRemove > 0 && (
                            <div>❌ {tense === 'future' ? 'Items to Remove' : 'Items Removed'}: {nodeData.itemsToRemove}</div>
                        )}
                    </div>
                </div>
            ))}
            {hasVisiblePluginContent(pluginData) && (
                <div className={styles.syncStatusPluginMessages}>
                    {Object.entries(pluginData).map(([pluginName, data]) => (
                        <PluginMessages key={pluginName} pluginName={pluginName} pluginData={data} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SyncStatusPanel({
    remoteName,
    syncRecord,
    diffReview,
    syncError,
    isSyncInProgress,
    onDismissError,
    onDiscardDiff,
    selectedUuids,
    onSelectionChange,
}) {
    const mode = deriveSyncPanelMode({
        syncRecord,
        diffReview,
        syncError,
        isSyncInProgress,
    });

    if (mode === 'idle') {
        return null;
    }

    if (mode === 'diffReview' && !diffReview?.diffResult) {
        return null;
    }

    const titles = {
        diffReview: hasAnyStatisticsChanges(
            diffReview?.statistics,
            diffReview?.pluginPreview,
        ) ? 'Changes available' : 'No changes',
        syncing: `Syncing with ${remoteName}…`,
        complete: 'Sync complete',
        noop: 'No changes detected',
        error: 'Sync failed',
    };

    const panelClass = {
        diffReview: styles.syncPanelReview,
        syncing: styles.syncPanelSyncing,
        complete: styles.syncPanelComplete,
        noop: styles.syncPanelNoop,
        error: styles.syncPanelError,
    }[mode] || styles.syncPanel;

    const renderBody = () => {
        if (mode === 'error') {
            return (
                <div className={styles.syncStatusErrorText}>
                    {syncError || syncRecord?.error || 'Unknown error'}
                </div>
            );
        }

        if (mode === 'diffReview' && diffReview) {
            const hasChanges = hasAnyStatisticsChanges(
                diffReview.statistics,
                diffReview.pluginPreview
            );
            if (!hasChanges) {
                return <div>No changes to sync</div>;
            }
            return (
                <DiffSelector
                    diffResult={diffReview.diffResult}
                    statistics={diffReview.statistics}
                    pluginPreview={diffReview.pluginPreview}
                    selectedUuids={selectedUuids}
                    onSelectionChange={onSelectionChange}
                />
            );
        }

        if (mode === 'noop') {
            return <div>No changes detected</div>;
        }

        const record = syncRecord || {};
        const stats = record.statistics || {};
        const plugin = record.pluginData || {};
        const tense = mode === 'syncing' ? 'future' : 'past';
        const { summaryText } = computeTotalsFromStatistics(stats, tense);

        return (
            <>
                <div className={styles.syncStatusCompletedText}>{summaryText}</div>
                {record.completedAt && mode === 'complete' && (
                    <div className={styles.syncStatusCompletedTimestamp}>
                        {new Date(record.completedAt).toLocaleString()}
                    </div>
                )}
                {(mode === 'syncing' || mode === 'complete') && (
                    <NodeBreakdown
                        statistics={stats}
                        pluginData={plugin}
                        tense={tense}
                    />
                )}
            </>
        );
    };

    return (
        <div className={`${styles.syncPanel} ${panelClass}`}>
            <div className={styles.syncPanelHeader}>
                <div className={styles.syncPanelTitle}>{titles[mode]}</div>
                <div className={styles.syncPanelActions}>
                    {mode === 'diffReview' && onDiscardDiff && (
                        <button type="button" className={styles.syncPanelDismiss} onClick={onDiscardDiff} title="Discard">
                            ✕
                        </button>
                    )}
                    {mode === 'error' && onDismissError && (
                        <button type="button" className={styles.syncPanelDismiss} onClick={onDismissError} title="Dismiss">
                            ✕
                        </button>
                    )}
                </div>
            </div>
            <div className={styles.syncPanelBody}>
                {mode === 'syncing' && (
                    <div className={styles.syncStatusInProgressText}>⏳ Import in progress…</div>
                )}
                {renderBody()}
            </div>
        </div>
    );
}
